import { filter } from '@atlaskit/adf-utils/traverse';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

const isDate = (node: ADFEntity): boolean => {
	return node.type === 'date';
};

// MM-DD-YYYY, MM/DD/YYYY, M-DD-YYYY, M/DD/YYYY
// eg. 01-31-2024 or 1/31/2024
const datePatternMMDDYYYY: RegExp =
	/\b(0?[1-9]|1[0-2])[-\/](0?[1-9]|[12][0-9]|3[01])[-\/](\d{4})\b/;

// DD-MM-YYYY, DD/MM/YYYY, DD-M-YYYY, DD/M/YYYY
// eg. 31-12-2024 or 31/12/2024
const datePatternDDMMYYYY: RegExp =
	/\b(0?[1-9]|[12][0-9]|3[01])[-\/](0?[1-9]|1[0-2])[-\/](\d{4})\b/;

// YYYY-MM-DD, YYYY/MM/DD, YYYY-M-DD, YYYY/M/DD
// eg. 2024-12-31 or 2024-1-31
const datePatternYYYYMMDD: RegExp =
	/\b(\d{4})[-\/](0?[1-9]|1[0-2])[-\/](0?[1-9]|[12][0-9]|3[01])\b/;

const monthStrPattern =
	'(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)';

// Dec(ember) 31(st), 2024; Case insensitive
const datePatternMonthDayYear: RegExp = new RegExp(
	`\\b${monthStrPattern}\\s*(0?[1-9]|1[0-9]|2[0-9]|3[01])(st|nd|rd|th)?,?\\s*(\\d{4})\\b`,
	'i',
);

// 31(st) Dec(ember), 2024; Case insensitive
const datePatternDayMonthYear: RegExp = new RegExp(
	`\\b(0?[1-9]|1[0-9]|2[0-9]|3[01])(st|nd|rd|th)?\\s*${monthStrPattern}\\s*,?\\s*(\\d{4})\\b`,
	'i',
);

// Jan(uary) 31(st); Case insensitive
const datePatternMonthDay: RegExp = new RegExp(
	`\\b${monthStrPattern}\\s*(0?[1-9]|1[0-9]|2[0-9]|3[01])(st|nd|rd|th)?\\b`,
	'i',
);

// 31(st) Jan(uary); Case insensitive
const datePatternDayMonth: RegExp = new RegExp(
	`\\b(0?[1-9]|1[0-9]|2[0-9]|3[01])(st|nd|rd|th)?\\s*${monthStrPattern}\\b`,
	'i',
);

const parseDateWithTimeZoneOffset = (year: number, month: number, day: number): number | null => {
	const date = new Date(Date.UTC(year, month, day));

	return isNaN(date.getTime()) ? null : addTimezoneOffset(date.getTime());
};

// Returns the closest year.
// If the date passed in is in the past, then return next year.
// Otherwise return this year.
const getClosestYear = (month: number, day: number): number => {
	const currentDay = new Date();
	const currentYear = currentDay.getFullYear();
	const dateToVerify = new Date(currentYear, month, day);

	return currentDay > dateToVerify ? currentYear + 1 : currentYear;
};

export const getFirstDateInADF = (content: ADFEntity): number | null => {
	const dateNodes = filter(content, (node) => isDate(node));
	return dateNodes.length === 0 ? null : addTimezoneOffset(Number(dateNodes[0].attrs?.timestamp));
};

export const getFirstDateInTitle = (title: string, locale: string): number | null => {
	const isLocaleENGB = locale === 'en-GB';
	// Handle different date pattern
	if (!isLocaleENGB && datePatternMMDDYYYY.test(title)) {
		const [, month, day, year] = title.match(datePatternMMDDYYYY)!;
		return parseDateWithTimeZoneOffset(Number(year), Number(month) - 1, Number(day));
	}

	if (isLocaleENGB && datePatternDDMMYYYY.test(title)) {
		const [, day, month, year] = title.match(datePatternDDMMYYYY)!;
		return parseDateWithTimeZoneOffset(Number(year), Number(month) - 1, Number(day));
	}

	if (datePatternYYYYMMDD.test(title)) {
		const [, year, month, day] = title.match(datePatternYYYYMMDD)!;
		return parseDateWithTimeZoneOffset(Number(year), Number(month) - 1, Number(day));
	}

	if (datePatternMonthDayYear.test(title)) {
		const [, monthStr, day, , year] = title.match(datePatternMonthDayYear)!;
		const month = new Date(`${monthStr} 1`).getMonth();
		return parseDateWithTimeZoneOffset(Number(year), Number(month), Number(day));
	}

	if (datePatternDayMonthYear.test(title)) {
		const [, day, , monthStr, year] = title.match(datePatternDayMonthYear)!;
		const month = new Date(`${monthStr} 1`).getMonth();
		return parseDateWithTimeZoneOffset(Number(year), Number(month), Number(day));
	}

	if (datePatternDayMonth.test(title)) {
		const [, day, , monthStr] = title.match(datePatternDayMonth)!;
		const month = new Date(`${monthStr} 1`).getMonth();
		const dayNum = Number(day);
		const year = getClosestYear(Number(month), dayNum);
		return parseDateWithTimeZoneOffset(Number(year), Number(month), dayNum);
	}

	if (datePatternMonthDay.test(title)) {
		const [, monthStr, day] = title.match(datePatternMonthDay)!;
		const month = new Date(`${monthStr} 1`).getMonth();
		const dayNum = Number(day);
		const year = getClosestYear(Number(month), dayNum);
		return parseDateWithTimeZoneOffset(Number(year), Number(month), dayNum);
	}

	return null;
};

export const getFirstDate = (title: string, content: ADFEntity, locale: string): number | null => {
	const firstDateFromADF = getFirstDateInADF(content);
	return firstDateFromADF ? firstDateFromADF : getFirstDateInTitle(title, locale);
};

export const addTimezoneOffset = (date: number): number => {
	const rawDate = new Date(date);
	const offset = rawDate.getTimezoneOffset();
	return rawDate.getTime() + offset * 60 * 1000;
};
